import range from 'lodash/range';
import flow from 'lodash/flow';
import {
  getServerValidationErrors,
  mapPropsToValidators,
  reduceValidationResults,
  validate,
} from '../validators';
import { commonQuestionState, updateWhy } from '../response/reducer';
import { t } from '../../../utils/translate';
import { prefillMultipart } from '../prefillAnswers';

const addMultiPartState = (props, numFields) => state => ({
  ...state,
  composite_type: props.composite_type,
  fieldValids: range(numFields).map(() => true),
  values: getDefaultValues({
    composite_type: props.composite_type,
    numFields,
    required: props.required,
    tmap: props.tmap,
  }),
  validators: mapPropsToValidators(
    {
      required: props.required,
    },
    {
      required: required,
    }
  ),
});

const reducer = props => (state, action) => {
  if (state === undefined) {
    const numFields = getNumFields(props);
    return flow(
      commonQuestionState(props),
      getServerValidationErrors(props),
      addMultiPartState(props, numFields)
    )({});
  }

  switch (action.type) {
    case 'MP_CHANGE':
      return flow(
        updateValue(action),
        updateIsTouched(action),
        validate(action)
      )(state);

    case 'MP_BLUR':
      return flow(updateIsTouched(action), validate(action))(state);

    case 'MP_PHONE_CHANGE':
      return flow(updatePhone(action), validate(action))(state);

    case 'WHY_CHANGE':
      return flow(updateWhy(action), validate(action))(state);

    case 'SUBMIT':
      return flow(touchAll, validate(action))(state);

    default:
      return state;
  }
};
export default reducer;

const getNumFields = props => {
  switch (props.composite_type) {
    case 'us_address':
      return 5;
    case 'address':
      return 6;
    case 'age':
      return 3;
    case 'phone':
    default:
      return 3;
  }
};

const updateValue = action => state => ({
  ...state,
  values: state.values.map(
    (v, index) =>
      index === action.index
        ? {
            ...v,
            value: action.value,
          }
        : v
  ),
});

const updateIsTouched = action => state => ({
  ...state,
  values: state.values.map(
    (v, index) =>
      index === action.index
        ? {
            ...v,
            isTouched: true,
          }
        : v
  ),
});

const touchAll = state => ({
  ...state,
  values: state.values.map(value => ({
    ...value,
    isTouched: true,
  })),
});

const updatePhone = action => state => {
  const digits = [].concat(action.value.match(/\d+/g));
  return {
    ...state,
    values: state.values.map((v, index) => ({
      ...v,
      isTouched: v.isTouched || (digits[index] && digits[index].length),
      value: digits[index] || '',
    })),
  };
};

// default states

const genericValues = ({ composite_type, numFields, required, tmap }) =>
  range(numFields).map(index => ({
    isTouched: false,
    required,
    value: prefillMultipart(tmap, index, composite_type),
  }));

// Second field is optional for address types
const addressValues = ({ composite_type, numFields, required, tmap }) =>
  range(numFields).map(index => ({
    isTouched: false,
    required: index === 1 ? false : required,
    value: prefillMultipart(tmap, index, composite_type),
  }));

const getDefaultValues = props =>
  props.composite_type === 'us_address' || props.composite_type === 'address'
    ? addressValues(props)
    : genericValues(props);

// validations

const isFieldValid = field => {
  const hasValue = !!field.value.trim().length;
  const isRequired = field.required;
  return hasValue || !isRequired;
};

export const required = state => {
  const fieldValids = state.values.map(isFieldValid);
  const areAllFieldsFilled = fieldValids.every(v => v === true);
  const isForgiven = state.values.some(v => !v.isTouched);
  const isValid = areAllFieldsFilled || isForgiven;
  const errorMessages = isValid ? null : [t('must_fill_all')];
  return reduceValidationResults(state, {
    errorMessages,
    fieldValids,
    isValid,
  });
};
